import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Users, Search, Download, Trash2, Plus, Merge, Loader2, X,
  RefreshCw, MailPlus,
} from "lucide-react";
import {
  contactsList, contactsGet, contactsCreate, contactsPatch,
  contactsDelete, contactsMerge, contactsBackfill, contactsExportCsv,
} from "@/lib/api";
import { toast } from "sonner";

/**
 * ContactsPage — /contacts
 *
 * One clean, customer-managed address book that unifies every source
 * (Inbox, Search Intelligence, Local Visibility, Reputation, Growth
 * Operator). Every column, tag, and merge action is designed so a
 * non-technical business owner can maintain the list themselves.
 */

const SOURCE_TONE = {
  inbox: "bg-orange-50 text-orange-700 border-orange-200",
  reputation: "bg-amber-50 text-amber-800 border-amber-200",
  si: "bg-sky-50 text-sky-800 border-sky-200",
  lv: "bg-emerald-50 text-emerald-800 border-emerald-200",
  go: "bg-violet-50 text-violet-800 border-violet-200",
  manual: "bg-slate-50 text-slate-700 border-slate-200",
};

export default function ContactsPage() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [detail, setDetail] = useState(null);
  const [detailBusy, setDetailBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newC, setNewC] = useState({ name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);

  const load = async (query = "") => {
    try {
      const d = await contactsList({ q: query || undefined, limit: 300 });
      setData(d);
    } catch {
      toast.error("Sign in to view contacts.");
    }
  };

  useEffect(() => { document.title = "Contacts — WebsiteTech.pro"; load(); }, []);

  const openDetail = async (id) => {
    setDetailBusy(true);
    try {
      const d = await contactsGet(id);
      setDetail(d);
    } finally { setDetailBusy(false); }
  };

  const saveDetail = async () => {
    if (!detail) return;
    setDetailBusy(true);
    try {
      const patch = {
        name: detail.contact.name,
        phone: detail.contact.phone,
        email: detail.contact.email,
        tags: detail.contact.tags,
        notes: detail.contact.notes,
      };
      await contactsPatch(detail.contact.id, patch);
      toast.success("Saved");
      await load(q);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setDetailBusy(false); }
  };

  const removeContact = async (id) => {
    if (!confirm("Delete this contact? This cannot be undone.")) return;
    await contactsDelete(id);
    toast.success("Contact deleted");
    setDetail(null);
    await load(q);
  };

  const mergeSelected = async () => {
    if (selected.size < 2) {
      toast.error("Pick at least 2 contacts to merge");
      return;
    }
    const ids = Array.from(selected);
    const keep = ids[0];
    if (!confirm(`Merge ${ids.length - 1} contact(s) into the first one? `
      + "All linked history stays with the kept contact.")) return;
    setBusy(true);
    try {
      await contactsMerge(keep, ids.slice(1));
      toast.success(`Merged ${ids.length - 1} record(s)`);
      setSelected(new Set());
      await load(q);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Merge failed");
    } finally { setBusy(false); }
  };

  const backfill = async () => {
    setBusy(true);
    try {
      const r = await contactsBackfill();
      toast.success(`Scanned ${r.scanned}, upserted ${r.upserted}.`);
      await load(q);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Admin only");
    } finally { setBusy(false); }
  };

  const submitAdd = async () => {
    if (!newC.email && !newC.phone) {
      toast.error("Give at least an email or phone");
      return;
    }
    setBusy(true);
    try {
      await contactsCreate(newC);
      toast.success("Contact added");
      setShowAdd(false);
      setNewC({ name: "", email: "", phone: "" });
      await load(q);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="contacts-page">
      <Navbar />

      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-8">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-orange-600">
            <Users className="h-4 w-4" /> Contacts
          </div>
          <h1 className="mt-1 font-heading text-3xl font-black text-slate-900 sm:text-4xl">
            Your address book — one clean list
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">
            Every person who has ever contacted you — from the inbox, an
            audit form, a review, or a growth task — appears here once.
            Search, add tags, merge duplicates, and export whenever you like.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-6">
        {/* Toolbar */}
        <Card className="border border-slate-200 bg-white p-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search by name, email, phone, notes…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load(q)}
                className="w-full rounded-md border border-slate-300 py-2 pl-9 pr-3 text-sm"
                data-testid="contacts-search"
              />
            </div>
            <Button variant="outline" onClick={() => load(q)}
              data-testid="contacts-search-btn">
              <RefreshCw className="mr-1 h-4 w-4" /> Refresh
            </Button>
            <Button variant="outline" onClick={() => setShowAdd(true)}
              data-testid="contacts-add-btn">
              <Plus className="mr-1 h-4 w-4" /> Add contact
            </Button>
            <Button variant="outline"
              onClick={mergeSelected}
              disabled={selected.size < 2 || busy}
              data-testid="contacts-merge-btn">
              <Merge className="mr-1 h-4 w-4" /> Merge {selected.size || ""}
            </Button>
            <Button variant="outline" onClick={contactsExportCsv}
              data-testid="contacts-export-btn">
              <Download className="mr-1 h-4 w-4" /> Export CSV
            </Button>
            <Button variant="outline" onClick={backfill} disabled={busy}
              data-testid="contacts-backfill-btn">
              <MailPlus className="mr-1 h-4 w-4" /> Import from other modules
            </Button>
          </div>
          {data && (
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
              <span>{data.total} contacts total.</span>
              {Object.entries(data.tag_counts || {}).map(([tag, n]) => (
                <button
                  key={tag}
                  onClick={() => { setQ(tag); load(tag); }}
                  className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 hover:bg-slate-100"
                >{tag} · {n}</button>
              ))}
            </div>
          )}
        </Card>

        {/* List */}
        {!data ? (
          <div className="mt-6 flex items-center gap-2 text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : data.contacts.length === 0 ? (
          <Card className="mt-6 border-dashed border-slate-300 bg-white p-10 text-center">
            <p className="text-sm text-slate-600">
              No contacts yet. Import them with{" "}
              <button className="font-semibold text-orange-600 underline"
                onClick={backfill}>one click</button>{" "}or add someone manually.
            </p>
          </Card>
        ) : (
          <Card className="mt-6 overflow-hidden border border-slate-200 bg-white">
            <table className="w-full text-sm" data-testid="contacts-table">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="w-8 px-3 py-2"></th>
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Email · phone</th>
                  <th className="px-3 py-2">Sources</th>
                  <th className="px-3 py-2">Tags</th>
                  <th className="px-3 py-2">Last seen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.contacts.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50"
                    data-testid={`contact-row-${c.id}`}>
                    <td className="px-3 py-2">
                      <input type="checkbox"
                        checked={selected.has(c.id)}
                        onChange={() => {
                          const next = new Set(selected);
                          next.has(c.id) ? next.delete(c.id) : next.add(c.id);
                          setSelected(next);
                        }} />
                    </td>
                    <td className="px-3 py-2">
                      <button onClick={() => openDetail(c.id)}
                        className="font-medium text-slate-900 hover:text-orange-600">
                        {c.name || <span className="italic text-slate-400">no name</span>}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-slate-600">
                      {c.email && <div>{c.email}</div>}
                      {c.phone && <div className="text-xs text-slate-500">{c.phone}</div>}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {(c.sources || []).map((s) => (
                          <span key={s} className={`rounded-full border px-2 py-0.5 text-[10px] ${SOURCE_TONE[s] || SOURCE_TONE.manual}`}>{s}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {(c.tags || []).map((t) => (
                          <span key={t} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-700">{t}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">
                      {c.last_seen_at ? new Date(c.last_seen_at).toLocaleString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </section>

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          onClick={() => setDetail(null)}
          data-testid="contact-detail-modal">
          <Card className="max-h-[85vh] w-full max-w-2xl overflow-y-auto border border-slate-200 bg-white p-5"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Contact</div>
                <h2 className="mt-1 font-heading text-xl font-bold text-slate-900">
                  {detail.contact.name || detail.contact.email || detail.contact.phone}
                </h2>
              </div>
              <button onClick={() => setDetail(null)}
                className="rounded p-1 text-slate-400 hover:bg-slate-100">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <Field label="Name" v={detail.contact.name || ""}
                onChange={(v) => setDetail({ ...detail, contact: { ...detail.contact, name: v } })} />
              <Field label="Email" v={detail.contact.email || ""}
                onChange={(v) => setDetail({ ...detail, contact: { ...detail.contact, email: v } })} />
              <Field label="Phone" v={detail.contact.phone || ""}
                onChange={(v) => setDetail({ ...detail, contact: { ...detail.contact, phone: v } })} />
              <Field label="Tags (comma-separated)"
                v={(detail.contact.tags || []).join(", ")}
                onChange={(v) => setDetail({ ...detail, contact: { ...detail.contact, tags: v.split(",").map(s => s.trim()).filter(Boolean) } })} />
            </div>
            <div className="mt-3">
              <label className="text-xs font-semibold text-slate-500">Notes</label>
              <textarea rows={3}
                value={detail.contact.notes || ""}
                onChange={(e) => setDetail({ ...detail, contact: { ...detail.contact, notes: e.target.value } })}
                className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
            </div>
            {detail.linked && Object.keys(detail.linked).length > 0 && (
              <div className="mt-4 border-t border-slate-100 pt-3">
                <div className="text-xs font-semibold text-slate-500">Linked activity</div>
                {Object.entries(detail.linked).map(([kind, items]) => (
                  <div key={kind} className="mt-2">
                    <div className="text-xs font-medium text-slate-700">{kind.replace(/_/g, " ")}</div>
                    <ul className="ml-2 mt-1 text-xs text-slate-600">
                      {items.slice(0, 8).map((it) => (
                        <li key={it.id}>· {it.title || it.id.slice(0, 8)}{it.at ? ` — ${new Date(it.at).toLocaleDateString()}` : ""}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-5 flex flex-wrap justify-between gap-2 border-t border-slate-100 pt-4">
              <Button variant="outline"
                onClick={() => removeContact(detail.contact.id)}
                className="text-rose-600"
                data-testid="contact-delete-btn">
                <Trash2 className="mr-1 h-4 w-4" /> Delete
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setDetail(null)}>Cancel</Button>
                <Button className="bg-orange-600 text-white hover:bg-orange-700"
                  onClick={saveDetail} disabled={detailBusy}
                  data-testid="contact-save-btn">
                  {detailBusy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                  Save changes
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Add modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          onClick={() => setShowAdd(false)}
          data-testid="contact-add-modal">
          <Card className="w-full max-w-md border border-slate-200 bg-white p-5"
            onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-lg font-bold text-slate-900">Add a contact</h3>
            <div className="mt-3 space-y-3">
              <Field label="Name" v={newC.name}
                onChange={(v) => setNewC({ ...newC, name: v })} />
              <Field label="Email" v={newC.email}
                onChange={(v) => setNewC({ ...newC, email: v })} />
              <Field label="Phone" v={newC.phone}
                onChange={(v) => setNewC({ ...newC, phone: v })} />
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button className="bg-orange-600 text-white hover:bg-orange-700"
                onClick={submitAdd} disabled={busy}
                data-testid="contact-add-submit">Add</Button>
            </div>
          </Card>
        </div>
      )}

      <Footer />
    </div>
  );
}

function Field({ label, v, onChange }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-500">{label}</label>
      <input
        type="text" value={v || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
    </div>
  );
}
